import React from 'react';

import { AuthProvider } from '../components/AuthProvider';
import { LayoutProvider } from '../components/LayoutProvider';
import { Message } from '../components/Message';
import CookieBanner from '../components/CookieBanner';

import Start from './Start';
import About from './About';
import Contact from './Contact';
import Invest from './Invest';
import Terms from './Terms';
import Login from './Login';
import BankIdLogin from './BankIdLogin';
import Home from './Home';
import Printer from './Printer';
import Scans from './Scans';
import UserAdmin from './UserAdmin';
import Register from './Register';
import RegisterQrCode from './RegisterQrCode';
import Bearers from './Bearers';
import EditDiseases from './EditDiseases';
import Layout from './Layout';
import NotFound from './NotFound';

import { AuthContext } from '../components/AuthProvider';

import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	useLocation
} from "react-router-dom";

/**
 * Routes only available when not logged in
 */
const UnloggedInRoute = ({ children }) => {
    const { loginToken }	= React.useContext(AuthContext);
	const location			= useLocation();
	if(loginToken) {
		// Preserve an in-progress wristband registration: a user who is already
		// logged in (e.g. after a BankID same-device redirect reload) must land on
		// the QR registration page, not the default home route.
		const pending_token = new URLSearchParams(location.search).get('pending_token');
		const target = pending_token ? ('/register_qr_code?pending_token=' + pending_token) : '/home';
		return (<Navigate to={target} replace state={{ from:location }}/>);
	}
	return children;
}

/**
 * Routes only available to authenticated users
 */
const ProtectedRoute = ({ children }) => {
    const { loginToken }	= React.useContext(AuthContext);
	const location			= useLocation();

	const queryParams = new URLSearchParams(location.search);
	const pending_token = queryParams.get('pending_token');
	const navigateTo = pending_token ? ('/login?pending_token=' + pending_token) : '/login';

	if(!loginToken) {
		return (<Navigate to={navigateTo} replace state={{ from:location }}/>);
	}
	return children;
}

/**
 * Routes only available to PRINTER, ADMIN and SUPERUSER users
 */
const PrinterRoute = ({ children }) => {
    const { loginToken, userType }	= React.useContext(AuthContext);
	const location					= useLocation();
	if(loginToken && (userType == 'PRINTER' || userType == 'ADMIN' || userType == 'SUPERUSER')) {
		return children;
	}
	return (<Navigate to="/home" replace state={{ from:location }}/>);
}

const App = () => {
	return (
		<React.StrictMode>
			<Router>
				<AuthProvider>
					<LayoutProvider>
						<Layout>
							<CookieBanner />
							<Message />
							<Routes>
								<Route index element={<Start />} />
								<Route path="start" element={<Start />} />
								<Route path="about" element={<About />} />
								<Route path="contact" element={<Contact />} />
								<Route path="terms" element={<Terms />} />
								<Route path="invest" element={<UnloggedInRoute><Invest /></UnloggedInRoute>} />
								<Route path="login" element={<UnloggedInRoute><BankIdLogin /></UnloggedInRoute>} />
								<Route path="admin" element={<UnloggedInRoute><Login /></UnloggedInRoute>} />
								<Route path="home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
								<Route path="printer" element={<PrinterRoute><Printer /></PrinterRoute>} />
								<Route path="user_admin" element={<ProtectedRoute><UserAdmin /></ProtectedRoute>} />
								<Route path="scans" element={<ProtectedRoute><Scans /></ProtectedRoute>} />
								<Route path="bearers" element={<ProtectedRoute><Bearers /></ProtectedRoute>} />
								<Route path="diseases" element={<ProtectedRoute><EditDiseases /></ProtectedRoute>} />
								<Route path="register_qr_code" element={<ProtectedRoute><RegisterQrCode /></ProtectedRoute>} />
								<Route path="*" element={<NotFound />} />
							</Routes>
						</Layout>
					</LayoutProvider>
				</AuthProvider>
			</Router>
		</React.StrictMode>
	);
}

export default App;
